import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { ChevronDown, Close } from "@dreamhub/designsystem/icons";
import { Button } from "@/components/ui/button";
import { IconButton } from "@/components/ui/icon-button";
import Text from "@/components/ui/text";
import IconContainer from "@/components/ui/icon-container";
import { formatDateShort } from "@/pages/deals/components/commercials/DealCommercialsV2/ContractTermsV2/consts";
import ShiftDatesDropdown, { ShiftDatesSelection } from "./ShiftDatesDropdown";
import { DateShift, DEFAULT_SHIFT_PRESET } from "./types";

interface ShiftDatesBarProps {
  appliesToAll: boolean;
  onApplyShift: (shift: DateShift) => void;
  onApplyStartDate: (date: Date) => void;
  onClose: () => void;
}

const ShiftDatesBar: React.FC<ShiftDatesBarProps> = ({
  appliesToAll,
  onApplyShift,
  onApplyStartDate,
  onClose
}) => {
  const { t } = useTranslation();
  const [selection, setSelection] = useState<ShiftDatesSelection>({
    kind: "preset",
    shift: DEFAULT_SHIFT_PRESET
  });

  const presetLabel = (shift: DateShift) =>
    t(`contracts.duplicate.shiftDates.presets.${shift.unit}`, {
      count: shift.amount
    });

  const isDateMode = selection.kind === "date";

  const handleApply = () => {
    if (selection.kind === "date") {
      onApplyStartDate(selection.date);
      return;
    }
    onApplyShift(selection.shift);
  };

  return (
    <div
      className="flex items-center gap-3 flex-1"
      data-test="duplicate-shift-dates-bar"
      onKeyDown={(event) => {
        if (event.key !== "Enter") return;
        // Buttons turn Enter into a click of their own, so handling it here
        // as well would apply the shift twice.
        if ((event.target as HTMLElement).closest("button")) return;
        handleApply();
      }}
    >
      <Text variant="body" className="whitespace-nowrap">
        {isDateMode
          ? t("contracts.duplicate.shiftDates.moveToStartOn")
          : t(
              appliesToAll
                ? "contracts.duplicate.shiftDates.forwardByAll"
                : "contracts.duplicate.shiftDates.forwardBySelected"
            )}
      </Text>
      <ShiftDatesDropdown
        selection={selection}
        onSelect={setSelection}
        trigger={
          <Button
            variant="secondary"
            className="justify-between flex-1 font-normal"
            data-test="duplicate-shift-dates-select"
          >
            <Text variant="body" className="truncate">
              {isDateMode
                ? formatDateShort(selection.date.toISOString())
                : presetLabel(selection.shift)}
            </Text>
            <IconContainer size="sm" color="secondary">
              <ChevronDown />
            </IconContainer>
          </Button>
        }
      />
      <Button
        variant="primary"
        onClick={handleApply}
        data-test="duplicate-shift-apply"
      >
        {t("contracts.duplicate.shiftDates.apply")}
      </Button>
      <IconButton
        variant="iconNoBg"
        size="md"
        iconColor="secondary"
        onClick={onClose}
        data-test="duplicate-shift-bar-close"
      >
        <Close />
      </IconButton>
    </div>
  );
};

export default ShiftDatesBar;
